import { getZrmListingInfo, getZrmUserInfo } from '@zg-rentals/clickstream';
import { TRIGGER_LOCATIONS } from '../../constants/ClickstreamConstants';
import { trackEvent } from '../../../client/analytics';

import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

const EVENT_IDS = {
  CLICK_SAMPLE_APPLICATION_LINK_ON_PAGE: '4549',
  CLICK_SAMPLE_APPLICATION_LINK_IN_MODAL: '4550',
  CLICK_EDIT_ACCEPT_APPLICATIONS: '4551',
  CLICK_APPLICATIONS_NEXT_STEP: '5073',
  CLICK_INVITE_ONLY: '5074',
  CLICK_ENABLE_DIRECTLY: '5075',
  CLICK_DONT_USE_APPLICATIONS: '5076',
  CLICK_ENABLE_APPLICATIONS: '5330',
} as const;

// https://dataportal.zillowgroup.net/event-registry/4549
const ClickSampleApplicationLinkOnPage = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_SAMPLE_APPLICATION_LINK_ON_PAGE,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'sample_application',
    },
    semantic: {
      semantic_event_nm: 'view_sample_application',
      topic_tag_txt: ['rental_application'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4550
const ClickSampleApplicationLinkInModal = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_SAMPLE_APPLICATION_LINK_IN_MODAL,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'edit_enable_application',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'sample_application',
    },
    semantic: {
      semantic_event_nm: 'view_sample_application',
      topic_tag_txt: ['rental_application'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4551
const ClickEditAcceptApplications = ({
  currentProperty,
  user,
  triggerLocation,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerLocation: (typeof TRIGGER_LOCATIONS)[keyof typeof TRIGGER_LOCATIONS];
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_EDIT_ACCEPT_APPLICATIONS,
      event_type_version_id: '3',
    },
    clickstream_trigger: {
      trigger_location_nm: triggerLocation,
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'applications_module_inviteonly',
      trigger_source_nm: 'edit_applications',
    },
    semantic: {
      semantic_event_nm: 'edit_application_setting',
      topic_tag_txt: ['rental_application'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// deprecate
// https://dataportal.zillowgroup.net/event-registry/5073
const ClickApplicationsNextStep = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_APPLICATIONS_NEXT_STEP,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_property_overview',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'next_step',
      trigger_source_nm: 'enable_zillow_applications',
    },
    semantic: {
      semantic_event_nm: 'edit_application_setting',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/5074
const ClickInviteOnly = ({
  currentProperty = {},
  user,
  triggerLocation,
}: {
  currentProperty?: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerLocation: (typeof TRIGGER_LOCATIONS)[keyof typeof TRIGGER_LOCATIONS];
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_INVITE_ONLY,
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_location_nm: triggerLocation,
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'applications_module_inviteonly',
      trigger_source_nm: 'invite_only',
    },
    semantic: {
      semantic_event_nm: 'enable_invite_only',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/5075
const ClickEnableDirectly = ({
  currentProperty = {},
  user,
  triggerLocation,
}: {
  currentProperty?: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerLocation: (typeof TRIGGER_LOCATIONS)[keyof typeof TRIGGER_LOCATIONS];
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_ENABLE_DIRECTLY,
      event_type_version_id: '3',
    },
    clickstream_trigger: {
      trigger_location_nm: triggerLocation,
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'applications_module_inviteonly',
      trigger_source_nm: 'directly',
    },
    semantic: {
      semantic_event_nm: 'enable_applications',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/5076
const ClickDontUseApplications = ({
  currentProperty = {},
  user,
  triggerLocation,
}: {
  currentProperty?: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerLocation: (typeof TRIGGER_LOCATIONS)[keyof typeof TRIGGER_LOCATIONS];
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_DONT_USE_APPLICATIONS,
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_location_nm: triggerLocation,
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'applications_module_inviteonly',
      trigger_source_nm: 'no_dont_use',
    },
    semantic: {
      semantic_event_nm: 'disable_applications',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/5330
const ClickEnableApplications = ({
  currentProperty,
  user,
  triggerLocation,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerLocation: (typeof TRIGGER_LOCATIONS)[keyof typeof TRIGGER_LOCATIONS];
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_ENABLE_APPLICATIONS,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: triggerLocation,
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'enable_applications',
    },
    semantic: {
      semantic_event_nm: 'enable_applications',
      topic_tag_txt: [],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

const ApplicationsCSEvents = {
  ClickSampleApplicationLinkOnPage,
  ClickSampleApplicationLinkInModal,
  ClickEditAcceptApplications,
  ClickEnableApplications,
  ClickApplicationsNextStep,
  ClickEnableDirectly,
  ClickInviteOnly,
  ClickDontUseApplications,
  TRIGGER_LOCATIONS,
};

// Clickstream visualizer data-eventid attributes
ClickSampleApplicationLinkOnPage.data_event_id = EVENT_IDS.CLICK_SAMPLE_APPLICATION_LINK_ON_PAGE;
ClickSampleApplicationLinkInModal.data_event_id = EVENT_IDS.CLICK_SAMPLE_APPLICATION_LINK_IN_MODAL;
ClickEditAcceptApplications.data_event_id = EVENT_IDS.CLICK_EDIT_ACCEPT_APPLICATIONS;
ClickEnableApplications.data_event_id = EVENT_IDS.CLICK_ENABLE_APPLICATIONS;
ClickApplicationsNextStep.data_event_id = EVENT_IDS.CLICK_APPLICATIONS_NEXT_STEP;
ClickEnableDirectly.data_event_id = EVENT_IDS.CLICK_ENABLE_DIRECTLY;
ClickInviteOnly.data_event_id = EVENT_IDS.CLICK_INVITE_ONLY;
ClickDontUseApplications.data_event_id = EVENT_IDS.CLICK_DONT_USE_APPLICATIONS;

export default ApplicationsCSEvents;
